import {
  cryptoUint32,
  pick,
  sample,
  type Uint32Source,
} from "@/lib/tool-kit/random-fun/rng";

export type CaseMode = "uppercase" | "lowercase" | "both";

const ALPHABET = "abcdefghijklmnopqrstuvwxyz".split("");
/** Anything that is not an ASCII letter separates tokens: spaces, commas, digits, dots. */
const NON_LETTERS = /[^A-Za-z]+/;

/**
 * The letters the reader asked to exclude: single letters separated by spaces, commas or
 * any other punctuation. A longer token such as the "and" in "q, x, and z" is a word, not
 * three exclusions, so it is skipped.
 */
export function parseExcluded(input: string): Set<string> {
  const excluded = new Set<string>();
  for (const token of input.split(NON_LETTERS)) {
    if (token.length === 1) {
      excluded.add(token.toLowerCase());
    }
  }
  return excluded;
}

export function buildPool(caseMode: CaseMode, excluded: Set<string>): string[] {
  const base = ALPHABET.filter((letter) => !excluded.has(letter));
  if (caseMode === "lowercase") {
    return base;
  }
  const upper = base.map((letter) => letter.toUpperCase());
  if (caseMode === "uppercase") {
    return upper;
  }
  return [...base, ...upper];
}

/** `count` letters from the pool: independent draws, or a sample without repeats. */
export function drawLetters(
  pool: readonly string[],
  count: number,
  allowRepeats: boolean,
  source: Uint32Source = cryptoUint32
): string[] {
  if (!allowRepeats) {
    return sample(pool, count, source);
  }
  const values: string[] = [];
  for (let i = 0; i < count; i += 1) {
    values.push(pick(pool, source));
  }
  return values;
}
